import React, { useCallback, useEffect, useRef, useState } from 'react';
import { backupService } from '../services/api';
import { useApp } from '../context/AppContext';

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

const TABLE_LABELS = {
  StaffAccount: 'Staff accounts',
  Batch: 'Batches',
  Enrollment: 'Enrollments',
  StudentCredential: 'Student logins',
  ClassMaterial: 'Materials',
  ClassNote: 'Attendance notes',
  Attendance: 'Attendance records',
};

const Backup = () => {
  const { currentUser, notify } = useApp();
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [file, setFile] = useState(null);
  const [restoreFiles, setRestoreFiles] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const loadStats = useCallback(() => {
    backupService.stats().then(setStats).catch((err) => notify(err.message, 'error'));
  }, [notify]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await backupService.download();
      notify('Backup downloaded', 'success');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async (e) => {
    e.preventDefault();
    if (!file || confirmText !== 'RESTORE') return;
    const confirmed = window.confirm(
      'This overwrites all current staff, batches, enrollments, attendance, and materials with the contents of this backup. This cannot be undone. Continue?'
    );
    if (!confirmed) return;

    setRestoring(true);
    setResult(null);
    try {
      const res = await backupService.restore(file, restoreFiles);
      setResult({ type: 'success', message: res.message, snapshot: res.snapshot });
      notify('Restore complete', 'success');
      setFile(null);
      setConfirmText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadStats();
    } catch (err) {
      setResult({ type: 'error', message: err.message });
      notify(err.message, 'error');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="page">
      <h1>Backup &amp; Restore</h1>

      <div className="card">
        <h2>Backup</h2>
        <p className="stat-note">
          Downloads everything this app owns — staff accounts, batches, enrollments, attendance records, class
          materials, and uploaded PDF files — as a single .zip. Doesn't include student/customer records, which
          live in and are backed up by berlinr-backoffice.
        </p>
        {stats && (
          <ul className="list">
            {Object.entries(stats.tables).map(([key, count]) => (
              <li key={key}>
                <span>{TABLE_LABELS[key] || key}</span>
                <span className="batch-meta">{count}</span>
              </li>
            ))}
            <li>
              <span>Uploaded material files</span>
              <span className="batch-meta">
                {stats.uploads.fileCount} file{stats.uploads.fileCount === 1 ? '' : 's'} — {formatBytes(stats.uploads.totalBytes)}
              </span>
            </li>
          </ul>
        )}
        <button type="button" className="btn-primary" onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Preparing backup…' : 'Download Backup'}
        </button>
      </div>

      {currentUser.isSuperAdmin && (
        <div className="card restore-card">
          <h2>Restore from Backup</h2>
          <p className="stat-note">
            Replaces all current staff, batches, enrollments, attendance, and (optionally) material files with
            whatever's in the uploaded backup. A safety snapshot of the current data is taken automatically
            before anything is overwritten, but this still isn't reversible from the app itself — make sure
            the file is the one you mean to restore.
          </p>
          <form className="inline-form" onSubmit={handleRestore}>
            <input
              type="file"
              accept=".zip,application/zip"
              ref={fileInputRef}
              onChange={(e) => setFile(e.target.files[0] || null)}
              required
            />
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={restoreFiles}
                onChange={(e) => setRestoreFiles(e.target.checked)}
              />
              Also restore uploaded files
            </label>
            {file && (
              <input
                type="text"
                placeholder='Type "RESTORE" to confirm'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            )}
            <button
              type="submit"
              className="btn-primary btn-danger-solid"
              disabled={!file || confirmText !== 'RESTORE' || restoring}
            >
              {restoring ? 'Restoring…' : 'Restore from Backup'}
            </button>
          </form>

          {result && (
            <p className={result.type === 'error' ? 'stat-note restore-result-error' : 'stat-note restore-result-success'}>
              {result.message}
              {result.snapshot && ` — a pre-restore snapshot was saved on the server as "${result.snapshot}".`}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Backup;
